import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Alert, Tabs, Tab, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../../components/Navbar';
import formService from '../../services/formService';
import pdfService from '../../services/pdfService';
import usePagination from '../../hooks/usePagination';
import TablePagination from '../../components/TablePagination';

const SeniorInspectorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [stats, setStats] = useState({
    pendingReview: 0,
    reviewed: 0,
    sentBack: 0,
    myTotal: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadingPDF, setLoadingPDF] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewComments, setReviewComments] = useState('');

  const {
    page: pendingPage,
    setPage: setPendingPage,
    totalPages: pendingTotalPages,
    paginatedItems: paginatedPendingReviews
  } = usePagination(pendingReviews);

  const {
    page: mySubsPage,
    setPage: setMySubsPage,
    totalPages: mySubsTotalPages,
    paginatedItems: paginatedMySubmissions
  } = usePagination(mySubmissions);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await formService.getSubmissions();

      if (response.success) {
        const allSubmissions = response.submissions;

        // Separate pending reviews (from junior inspectors) and my submissions (senior's own)
        const pending = allSubmissions.filter(
          s => s.current_status === 'submitted_by_junior' && s.submitted_by !== user?.userid
        );

        const myOwn = allSubmissions.filter(
          s => s.submitted_by === user?.userid
        );

        const pendingReviewsData = pending.map(submission => ({
          id: submission.submission_id,
          form_number: submission.form_number,
          work_order_number: submission.work_order_number,
          client_name: submission.client_name,
          equipment_type: submission.equipment_type,
          inspection_date: submission.inspection_date,
          submitted_by: submission.submitted_by_name,
          status: submission.current_status,
          submitted_at: submission.created_at,
          certificate_number: submission.certificate_number || ''
        }));

        const mySubmissionsData = myOwn.map(submission => ({
          id: submission.submission_id,
          form_number: submission.form_number,
          work_order_number: submission.work_order_number,
          client_name: submission.client_name,
          equipment_type: submission.equipment_type,
          inspection_date: submission.inspection_date,
          status: submission.current_status,
          submitted_at: submission.created_at,
          certificate_number: submission.certificate_number || ''
        }));

        setPendingReviews(pendingReviewsData);
        setMySubmissions(mySubmissionsData);

        // Calculate stats
        // Backend now returns all submissions reviewed by this senior inspector
        setStats({
          pendingReview: pendingReviewsData.length,
          reviewed: allSubmissions.filter(s =>
            (s.current_status === 'reviewed_by_senior' || s.current_status === 'approved_by_admin') &&
            s.reviewed_by === user?.userid
          ).length,
          sentBack: allSubmissions.filter(s => s.current_status === 'sent_back_by_senior').length,
          myTotal: mySubmissionsData.length
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'submitted_by_junior': <Badge bg="warning" text="dark">Pending Review</Badge>,
      'sent_back_by_senior': <Badge bg="danger">Sent Back</Badge>,
      'reviewed_by_senior': <Badge bg="info">Sent to Admin</Badge>,
      'approved_by_admin': <Badge bg="success">Approved</Badge>
    };
    return badges[status] || <Badge bg="secondary">{status}</Badge>;
  };

  const handleReviewClick = (submission) => {
    setSelectedSubmission(submission);
    setShowReviewModal(true);
    setReviewAction('');
    // Pre-fill comments from sticky notes saved while viewing the form
    const savedNotes = localStorage.getItem(`sr_notes_${submission.id}`);
    setReviewComments(savedNotes || '');
  };

  const handleSubmitReview = async () => {
    try {
      const response = await formService.reviewSubmission(
        selectedSubmission.id,
        reviewAction,
        reviewComments
      );

      if (response.success) {
        // Clear saved notes for this submission now that review is submitted
        localStorage.removeItem(`sr_notes_${selectedSubmission.id}`);
        setShowReviewModal(false);
        await fetchSubmissions();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    }
  };

  const handleViewDetails = (submissionId) => {
    navigate(`/submissions/${submissionId}`);
  };

  const handleViewDataOnly = async (submission) => {
    try {
      setLoadingPDF(true);

      // Generate data-only preview (without seal and signatures)
      const response = await pdfService.dataOnlyPreview(submission.id);

      if (response.success) {
        // Download the data-only preview immediately
        const blob = await pdfService.downloadPreview(response.docx_filename);

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.docx_filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        alert('Data-only preview downloaded successfully! (No seal or signatures included)');
      } else {
        alert('Error generating data-only preview: ' + response.message);
      }
    } catch (error) {
      console.error('Error generating data-only preview:', error);
      alert('Failed to generate data-only preview. Please try again.');
    } finally {
      setLoadingPDF(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="mt-5 text-center" style={{ paddingTop: '80px' }}>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading dashboard...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container fluid className="mt-4 mb-5" style={{ paddingTop: '40px' }}>
        {/* Welcome Section */}
        <Row className="mb-4">
          <Col>
            <h2 className="fw-bold text-primary">
              <i className="fas fa-user-tie me-2"></i>
              Reviewer Dashboard
            </h2>
            <p className="text-muted">
              Welcome back, <strong>{user?.full_name}</strong>! ({user?.emp_code})
            </p>
          </Col>
        </Row>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100 border-warning">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-clock text-warning" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.pendingReview}</h3>
                <p className="text-muted mb-0">Pending Review</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-check-double text-info" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.reviewed}</h3>
                <p className="text-muted mb-0">Reviewed</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-undo text-danger" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.sentBack}</h3>
                <p className="text-muted mb-0">Sent Back</p>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center shadow-sm h-100">
              <Card.Body>
                <div className="mb-2">
                  <i className="fas fa-clipboard-list text-primary" style={{ fontSize: '2.5rem' }}></i>
                </div>
                <h3 className="fw-bold mb-0">{stats.myTotal}</h3>
                <p className="text-muted mb-0">My Submissions</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex gap-3">
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/new-inspection')}
                className="shadow"
              >
                <i className="fas fa-plus-circle me-2"></i>
                New Inspection Form
              </Button>
              <Button
                variant="info"
                size="lg"
                onClick={() => navigate('/my-drafts')}
                className="shadow"
              >
                <i className="fas fa-save me-2"></i>
                My Drafts
              </Button>
            </div>
          </Col>
        </Row>

        {/* Tabs for different sections */}
        <Row>
          <Col>
            <Tabs defaultActiveKey="pending" className="mb-3">
              {/* Pending Reviews Tab */}
              <Tab eventKey="pending" title={`Pending Reviews (${stats.pendingReview})`}>
                <Card className="shadow">
                  <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">
                      <i className="fas fa-tasks me-2"></i>
                      Forms Submitted by Inspectors
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {pendingReviews.length === 0 ? (
                      <Alert variant="info">
                        <i className="fas fa-info-circle me-2"></i>
                        No pending reviews at the moment.
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Form Number</th>
                              <th>Work Order No</th>
                              <th>Client</th>
                              <th>Equipment</th>
                              <th>Inspection Date</th>
                              <th>Submitted By</th>
                              <th>Submitted At</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedPendingReviews.map((submission) => (
                              <tr key={submission.id}>
                                <td>
                                  <div className="fw-bold">{submission.form_number}</div>
                                  {submission.certificate_number && (
                                    <div className="text-muted small mt-1">
                                      <i className="fas fa-certificate me-1" style={{ fontSize: '0.7rem' }}></i>
                                      {submission.certificate_number}
                                    </div>
                                  )}
                                </td>
                                <td>{submission.work_order_number || '-'}</td>
                                <td>{submission.client_name}</td>
                                <td>{submission.equipment_type}</td>
                                <td>{new Date(submission.inspection_date).toLocaleDateString()}</td>
                                <td>{submission.submitted_by}</td>
                                <td>{new Date(submission.submitted_at).toLocaleString()}</td>
                                <td>{getStatusBadge(submission.status)}</td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2 mb-1"
                                    onClick={() => handleViewDetails(submission.id)}
                                    title="View Details"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Button>
                                  <Button
                                    variant="info"
                                    size="sm"
                                    className="me-2 mb-1"
                                    onClick={() => handleViewDataOnly(submission)}
                                    disabled={loadingPDF}
                                    title="View Data Only (No seal/signatures)"
                                  >
                                    <i className="fas fa-file-word"></i>
                                  </Button>
                                  <Button
                                    variant="warning"
                                    size="sm"
                                    className="mb-1"
                                    onClick={() => handleReviewClick(submission)}
                                  >
                                    <i className="fas fa-clipboard-check me-1"></i>
                                    Review
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <TablePagination currentPage={pendingPage} totalPages={pendingTotalPages} onPageChange={setPendingPage} />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* My Submissions Tab */}
              <Tab eventKey="my-submissions" title={`My Submissions (${stats.myTotal})`}>
                <Card className="shadow">
                  <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-user-check me-2"></i>
                      My Inspection Forms
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    {mySubmissions.length === 0 ? (
                      <Alert variant="info">
                        <i className="fas fa-info-circle me-2"></i>
                        No submissions yet. Click "New Inspection Form" to create one.
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Form Number</th>
                              <th>Work Order No</th>
                              <th>Client</th>
                              <th>Equipment</th>
                              <th>Inspection Date</th>
                              <th>Status</th>
                              <th>Submitted At</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedMySubmissions.map((submission) => (
                              <tr key={submission.id}>
                                <td>
                                  <div className="fw-bold">{submission.form_number}</div>
                                  {submission.certificate_number && (
                                    <div className="text-muted small mt-1">
                                      <i className="fas fa-certificate me-1" style={{ fontSize: '0.7rem' }}></i>
                                      {submission.certificate_number}
                                    </div>
                                  )}
                                </td>
                                <td>{submission.work_order_number || '-'}</td>
                                <td>{submission.client_name}</td>
                                <td>{submission.equipment_type}</td>
                                <td>{new Date(submission.inspection_date).toLocaleDateString()}</td>
                                <td>{getStatusBadge(submission.status)}</td>
                                <td>{new Date(submission.submitted_at).toLocaleString()}</td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2 mb-1"
                                    onClick={() => handleViewDetails(submission.id)}
                                    title="View Details"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Button>
                                  <Button
                                    variant="info"
                                    size="sm"
                                    className="mb-1"
                                    onClick={() => handleViewDataOnly(submission)}
                                    disabled={loadingPDF}
                                    title="View Data Only (No seal/signatures)"
                                  >
                                    <i className="fas fa-file-word"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <TablePagination currentPage={mySubsPage} totalPages={mySubsTotalPages} onPageChange={setMySubsPage} />
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Col>
        </Row>
      </Container>

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-clipboard-check me-2"></i>
            Review Submission
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubmission && (
            <>
              <Alert variant="info">
                <strong>Form Number:</strong> {selectedSubmission.form_number}<br />
                <strong>Client:</strong> {selectedSubmission.client_name}<br />
                <strong>Equipment:</strong> {selectedSubmission.equipment_type}<br />
                <strong>Submitted By:</strong> {selectedSubmission.submitted_by}
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Review Action <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={reviewAction}
                    onChange={(e) => setReviewAction(e.target.value)}
                    required
                  >
                    <option value="">-- Select Action --</option>
                    <option value="approve">Approve and Send to Admin</option>
                    <option value="send_back">Send Back to Inspector</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">
                    Comments {reviewAction === 'send_back' && <span className="text-danger">*</span>}
                    {reviewComments.trim() && (
                      <span className="ms-2 text-success" style={{ fontSize: '0.8em', fontWeight: 'normal' }}>
                        <i className="fas fa-sticky-note me-1"></i>Pre-filled from your notes
                      </span>
                    )}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    placeholder="Enter your comments here..."
                    value={reviewComments}
                    onChange={(e) => setReviewComments(e.target.value)}
                    required={reviewAction === 'send_back'}
                  />
                  {reviewAction === 'send_back' && !reviewComments.trim() && (
                    <Form.Text className="text-danger">
                      Comments are required when sending back to inspector
                    </Form.Text>
                  )}
                </Form.Group>
              </Form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitReview}
            disabled={!reviewAction || (reviewAction === 'send_back' && !reviewComments)}
          >
            <i className="fas fa-check me-2"></i>
            Submit Review
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SeniorInspectorDashboard;
